import { useEffect, useRef, useState } from "react";

/**
 * Custom hook to get the dimensions (width and height) of a container element.
 *
 * @template T - The type of the HTML element.
 * @returns A tuple containing:
 * - `ref` - A React ref object to be attached to the container element.
 * - `dimensions` - An object containing the width and height of the container element.
 *
 * @example
 * ```tsx
 * import React from 'react';
 * import { useContainerDimensions } from './useContainerDimensions';
 *
 * const MyComponent = () => {
 *   const [ref, dimensions] = useContainerDimensions<HTMLDivElement>();
 *
 *   return (
 *     <div ref={ref} style={{ width: '100%', height: '100%' }}>
 *       <p>Width: {dimensions.width}px</p>
 *       <p>Height: {dimensions.height}px</p>
 *     </div>
 *   );
 * };
 * ```
 */
export function useContainerDimensions<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setDimensions({ width, height });
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  return [ref, dimensions] as const;
}
